import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import { Badge } from 'src/shared/components/ui/badge';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import DataTable from 'src/shared/components/data-table/DataTable';
import {
    DataTableColumns,
    DataTableState,
    TableCellRendererParams,
    TableRowData,
    TableCellStructuredData,
    TableRowsSortingFunction,
} from 'src/shared/components/data-table/dataTableTypes';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import Tabs from 'src/shared/components/Tabs/Tabs';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip/IUCNStatusChip';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
import { SortOrder, Status } from 'src/shared/types';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import StatusChip from 'src/shared/components/status-chip/StatusChip';
import {
    usePredictedSpeciesTotalsQuery,
    useRedListSpeciesListQuery,
    useInvasiveSpeciesListQuery,
} from './state/api/speciesRiskIndicatorsGraphSlice';
import { SpeciesListItem, PaginatedSpeciesListResponse } from './state/api/speciesRiskIndicatorsQuery';
import SpeciesInfoModal from './components/species-info-modal/SpeciesInfoModal';
import ReportingFrameworks from './components/reporting-frameworks/ReportingFrameworks';
import usePortfolioSpeciesRiskAnalytics from './hooks/usePortfolioSpeciesRiskAnalytics';

const ROWS_PER_PAGE = 10;

const speciesListTabs = [
    {
        id: 'invasive-species',
        label: (
            <div className='flex items-center gap-2'>
                <CircleAlert className='text-red-100' /> Invasive species
            </div>
        ),
    },
    {
        id: 'iucn-red-list-species',
        label: (
            <div className='flex items-center gap-2'>
                <img src={IUCNLogo} alt='' className='w-[24px] h-[24x]' /> IUCN Red List species
            </div>
        ),
    },
];

const columnIdToSortField: Record<string, string> = {
    speciesName: 'commonName',
    scientificName: 'scientificName',
    added: 'predictionCreatedAt',
    siteCoverage: 'siteCount',
    redListStatus: 'iucnStatus',
};

/** Sorting is done on the server; keep row order so padded placeholder rows stay out of the current page slice. */
const preserveServerSortedRows: TableRowsSortingFunction = rows => [...rows];

type SpeciesRowMeta = {
    species: SpeciesListItem;
    percentCoverage: number;
    isNew?: boolean;
    isUpdated?: boolean;
};

/** Padded server-pagination rows use empty-string cells; real rows use `{ data: SpeciesRowMeta }` in column 0. */
const getSpeciesRowMeta = (rowData: TableRowData): SpeciesRowMeta | null => {
    const firstCell = rowData[0];
    if (firstCell === null || firstCell === undefined || typeof firstCell === 'string') {
        return null;
    }
    if (typeof firstCell !== 'object' || !('data' in firstCell)) {
        return null;
    }
    const meta = (firstCell as TableCellStructuredData).data as SpeciesRowMeta | undefined;
    if (!meta?.species) {
        return null;
    }
    return meta;
};

const iucnStatusRenderer = (params: TableCellRendererParams) => {
    if (!getSpeciesRowMeta(params.rowData)) {
        return null;
    }
    const status = params.cellData as string;
    if (!status) {
        return null;
    }
    return <IUCNStatusChip status={status as IUCNStatusCode} />;
};

const siteCoverageRenderer = (params: TableCellRendererParams) => {
    const meta = getSpeciesRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const { percentCoverage, species } = meta;
    return (
        <span className='flex items-cent gap-1'>
            <span className='font-bold'>{percentCoverage}%</span>({species.siteCount} site{species.siteCount > 1 ? 's' : ''})
        </span>
    );
};

const speciesNameRenderer = (params: TableCellRendererParams) => {
    const meta = getSpeciesRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const commonName = params.cellData as string;
    const badgeType = meta.isNew ? 'New' : meta.isUpdated ? 'Updated' : null;
    return (
        <div className='flex items-center gap-2'>
            <SpeciesImage
                scientificName={meta.species.scientificName}
                alt={commonName}
                width='100px'
                height='56.25px'
                className='rounded-lg'
            />
            <span className='text-primary'>{commonName}</span>
            {badgeType && (
                <QuestionButtonHelp
                    icon={
                        <Badge className='text-xs text-black font-normal bg-blue-20 hover:bg-blue-20 border border-blue-60'>
                            {badgeType}
                        </Badge>
                    }
                    type='api'
                    placement='top'
                    slug={`${badgeType.toLowerCase()}-species-prediction-badge`}
                    zIndex={1300}
                    maxWidth='400px'
                />
            )}
        </div>
    );
};

const redListColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    {
        columnId: 'speciesName',
        title: 'Species name',
        width: '30%',
        renderer: speciesNameRenderer,
        bodyCellClassName: 'cursor-pointer',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'scientificName',
        title: 'Scientific name',
        width: '20%',
        bodyCellClassName: 'cursor-pointer italic',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'added',
        title: 'Added',
        width: '10%',
        bodyCellClassName: 'cursor-pointer',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'redListStatus',
        title: 'IUCN Red List status',
        width: '20%',
        renderer: iucnStatusRenderer,
        bodyCellClassName: 'cursor-pointer',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'siteCoverage',
        title: 'Site coverage',
        width: '20%',
        renderer: siteCoverageRenderer,
        bodyCellClassName: 'cursor-pointer',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
];

const invasivesColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    { ...redListColumns[1], width: '40%' },
    { ...redListColumns[2], width: '20%' },
    { ...redListColumns[3], width: '20%' },
    { ...redListColumns[5], width: '20%' },
];

type SelectedRowData = {
    commonName: string;
    scientificName: string;
    siteCoverageInfo: { percentCoverage: number; siteCount: number };
    status: string;
};

export const SpeciesRiskIndicators = () => {
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = (searchParams.get('tab') as 'iucn-red-list-species' | 'invasive-species') || undefined;
    const urlSort = searchParams.get('sortDirection') as ('asc' | 'desc') | undefined;
    const initialSortOrder = urlSort === 'asc' ? ('ASC' as const) : ('DESC' as const);
    const initialSortDirection = urlSort === 'asc' ? SortingDirection.ASC : SortingDirection.DESC;

    const tabFromUrl = searchParams.get('tab') as string;
    const isValidUrlTab = speciesListTabs.some(tab => tab.id === tabFromUrl);
    const [selectedTab, setSelectedTab] = useState((isValidUrlTab && tabFromUrl) || speciesListTabs[0].id);

    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('siteCount');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(initialSortOrder || 'DESC');
    const [selectedRowData, setSelectedRowData] = useState<SelectedRowData | null>(null);
    /** Last response for this tab + page; used to avoid unmounting the table while sort refetches (new RTK cache key). */
    const [staleListPayload, setStaleListPayload] = useState<{
        tab: 'invasive-species' | 'iucn-red-list-species';
        page: number;
        data: PaginatedSpeciesListResponse;
    } | null>(null);

    const listQueryArgs = {
        customerId: currentCustomerId,
        pagination: { page: currentPage, pageSize: ROWS_PER_PAGE },
        sortBy: { field: sortField, order: sortOrder },
    };

    const { currentData: totalsData, isFetching: totalsFetching } = usePredictedSpeciesTotalsQuery(
        { customerId: currentCustomerId },
        { skip: !currentCustomerId }
    );

    const redListSkip = !currentCustomerId || selectedTab !== 'iucn-red-list-species';
    const invasiveSkip = !currentCustomerId || selectedTab !== 'invasive-species';

    const redListQuery = useRedListSpeciesListQuery(listQueryArgs, { skip: redListSkip });
    const invasiveQuery = useInvasiveSpeciesListQuery(listQueryArgs, { skip: invasiveSkip });

    const activeListQuery = selectedTab === 'invasive-species' ? invasiveQuery : redListQuery;
    const { currentData: listCurrentData, isFetching: listFetching } = activeListQuery;

    useEffect(() => {
        if (listCurrentData) {
            setStaleListPayload({
                tab: selectedTab as 'invasive-species' | 'iucn-red-list-species',
                page: currentPage,
                data: listCurrentData,
            });
        }
    }, [listCurrentData, selectedTab, currentPage]);

    const speciesListForTable: PaginatedSpeciesListResponse | null =
        listCurrentData ??
        (staleListPayload && staleListPayload.tab === selectedTab && staleListPayload.page === currentPage ? staleListPayload.data : null);

    const isFetching = totalsFetching || listFetching;
    const showFullPageLoader = isFetching && !speciesListForTable;
    const showTableLoadingOverlay = listFetching && !!speciesListForTable;

    const trackAnalytics = usePortfolioSpeciesRiskAnalytics();

    const buildTableData = useCallback(
        (species: SpeciesListItem[], numberSites: number) => {
            const isInvasiveTab = selectedTab === 'invasive-species';
            const isPredictionFirstRun = !totalsData || totalsData.predictionRunHistory.length < 2;
            const lastPredictionRunDate = totalsData?.lastPredictionRun ? totalsData.lastPredictionRun.split('T')[0] : '';
            return species.map(speciesItem => {
                const percentCoverage = numberSites > 0 ? Math.floor((speciesItem.siteCount / numberSites) * 100) : 0;
                const createdDate = speciesItem.predictionCreatedAt ? speciesItem.predictionCreatedAt.split('T')[0] : '';
                const updatedDate = speciesItem.predictionLastUpdated ? speciesItem.predictionLastUpdated.split('T')[0] : '';
                const isCreatedAndUpdatedSame = !updatedDate || updatedDate === createdDate;
                const isCreatedAtOrAfterLastRun = createdDate >= lastPredictionRunDate;
                const isUpdatedAtOrAfterLastRun = Boolean(updatedDate) && updatedDate >= lastPredictionRunDate;
                const isUpdatedAfterCreated = Boolean(updatedDate) && moment(createdDate).isBefore(moment(updatedDate));
                const meta: SpeciesRowMeta = {
                    species: speciesItem,
                    percentCoverage,
                    isNew: !isPredictionFirstRun && isCreatedAndUpdatedSame && isCreatedAtOrAfterLastRun,
                    isUpdated: !isPredictionFirstRun && isUpdatedAfterCreated && isUpdatedAtOrAfterLastRun,
                };
                const formattedDate = moment(speciesItem.predictionCreatedAt).format('MMM YYYY');

                if (isInvasiveTab) {
                    return [{ data: meta }, speciesItem.commonName, speciesItem.scientificName, formattedDate, speciesItem.siteCount];
                }
                return [
                    { data: meta },
                    speciesItem.commonName,
                    speciesItem.scientificName,
                    formattedDate,
                    speciesItem.iucnRedListCategory,
                    speciesItem.siteCount,
                ];
            });
        },
        [selectedTab, totalsData]
    );

    const totalItems = speciesListForTable?.pagination.total ?? 0;
    const totalPages = Math.ceil(totalItems / ROWS_PER_PAGE);

    const tableData = useMemo(
        () =>
            speciesListForTable && totalsData
                ? buildTableData(speciesListForTable.species, totalsData.numberSites)
                : speciesListForTable
                ? buildTableData(speciesListForTable.species, 0)
                : [],
        [speciesListForTable, totalsData, buildTableData]
    );

    const isRedListSpeciesTabActive = selectedTab === 'iucn-red-list-species';

    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['metadataRow']),
        rowsPerPage: ROWS_PER_PAGE,
        sortedColumn: { columnId: 'siteCoverage', sortedDirection: initialSortDirection },
    });

    useEffect(() => {
        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: currentPage,
            totalPages,
        }));
    }, [tableData, currentPage, totalPages]);

    const handleStateChange = (newState: Partial<DataTableState>) => {
        setTableState(newState);

        if (newState.currentPageNumber && newState.currentPageNumber !== currentPage) {
            setCurrentPage(newState.currentPageNumber);
        }

        if (newState.sortedColumn) {
            const newField = columnIdToSortField[newState.sortedColumn.columnId] || 'siteCount';
            const newOrder = newState.sortedColumn.sortedDirection === SortingDirection.ASC ? ('ASC' as const) : ('DESC' as const);
            if (newField !== sortField || newOrder !== sortOrder) {
                setSortField(newField);
                setSortOrder(newOrder);
                setCurrentPage(1);
            }
        }
    };

    const handleTabChange = (tab: string) => {
        trackAnalytics.trackTabClick({ tab });
        setStaleListPayload(null);
        setSelectedTab(tab as 'iucn-red-list-species' | 'invasive-species');
        setCurrentPage(1);
    };

    useEffect(() => {
        if (urlTab || urlSort) {
            setTimeout(() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('tab');
                newParams.delete('sortDirection');
                setSearchParams(newParams, { replace: true });
            }, 100);
        }
    }, [urlTab, urlSort]);

    const tableProps = {
        state: tableState,
        columns: isRedListSpeciesTabActive ? redListColumns : invasivesColumns,
        onStateChange: handleStateChange,
        onRowSelect: (rowData: TableRowData) => {
            const meta = getSpeciesRowMeta(rowData);
            if (!meta) {
                return;
            }
            setSelectedRowData({
                commonName: rowData[1] as string,
                scientificName: rowData[2] as string,
                siteCoverageInfo: {
                    percentCoverage: meta.percentCoverage,
                    siteCount: meta.species.siteCount,
                },
                status: isRedListSpeciesTabActive ? meta.species.iucnRedListCategory : 'IN',
            });
            trackAnalytics.trackSpeciesRiskModalOpen({ tab: selectedTab });
        },
        emptyStateMessage: `There are no ${
            isRedListSpeciesTabActive ? 'IUCN Red List' : 'Invasive'
        } species predicted to be present at this site`,
        className: 'overflow-visible',
        tableClassName: 'h-full',
        allowSortClear: false,
    };

    return (
        <StandardAppLayout
            mainContent={
                <>
                    {showFullPageLoader ? (
                        <Loader />
                    ) : (
                        <div className='flex flex-col gap-4'>
                            <div className='w-full border-b border-grey-20'>
                                <Tabs
                                    tabs={speciesListTabs}
                                    selectedTab={selectedTab}
                                    onTabChange={handleTabChange}
                                    classNames={{
                                        tabsContainer: 'flex items-center',
                                        default: 'px-3 py-4 text-[17px] text-black',
                                        selected: 'text-primary font-bold border-b-2 border-primary',
                                    }}
                                />
                            </div>

                            <div className='flex items-center px-4 py-2 bg-grey-20 rounded-lg text-black text-sm'>
                                <span>
                                    {totalsData?.totalSpeciesCount} predicted species across&nbsp;
                                    <a
                                        className='cursor-pointer text-primary hover:underline'
                                        onClick={() => {
                                            trackAnalytics.trackSitesLinkClick({ tab: selectedTab });
                                            navigate.toPortfolioSiteExplorer({
                                                customerId: currentCustomerId,
                                                view: 'list',
                                                selectedMetric:
                                                    selectedTab === 'invasive-species' ? 'predictedInvasive' : 'predictedRedList',
                                                sortDirection: SortOrder.DESC,
                                            });
                                        }}
                                    >
                                        {totalsData?.numberSites}
                                    </a>{' '}
                                    sites
                                </span>
                                <span className='border-l border-grey-60 mx-5 w-[1px]'>&nbsp;</span>
                                Last updated:{' '}
                                {totalsData?.lastPredictionRun ? moment(totalsData.lastPredictionRun).format('MMM YYYY') : '—'}
                                <span className='border-l border-grey-60 mx-5 w-[1px]'>&nbsp;</span>
                                <ReportingFrameworks />
                            </div>

                            <DataTable
                                {...tableProps}
                                key={selectedTab}
                                totalRowsForPagination={totalItems}
                                isServerSideSort
                                isLoadingOverlay={showTableLoadingOverlay}
                            />
                        </div>
                    )}
                    {selectedRowData && (
                        <SpeciesInfoModal
                            commonName={selectedRowData.commonName}
                            scientificName={selectedRowData.scientificName}
                            siteCoverage={selectedRowData.siteCoverageInfo}
                            statusChip={
                                isRedListSpeciesTabActive ? (
                                    <IUCNStatusChip status={selectedRowData.status as IUCNStatusCode} className='text-xs' />
                                ) : (
                                    <StatusChip status={Status.INVASIVE} label='Invasive' className='text-xs' />
                                )
                            }
                            onClose={() => setSelectedRowData(null)}
                        />
                    )}
                </>
            }
            title='Portfolio species risks'
            subTitle='Predicted species of interest across your portfolio'
        />
    );
};

export default SpeciesRiskIndicators;
